import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import useMe from '@/hooks/useMe.js';
import { ownerPagesVisible } from '@/lib/session.js';
import { formatHumanDate } from '@/lib/date-display.js';
import { getReviewList, getMergePreview, confirmMerge, markNotDuplicate, undoNotDuplicate } from '@/services/account-merge.service.js';

// ACCOUNT MERGE (2026-10-29 item 3; the review page, David's review items 1, 4, 5). Each pair that looks duplicated shows
// the two accounts side by side (name, created, policies with stage and term, people with role, aircraft). "Merge into this
// one" under an account keeps it and opens the preview of merging the other into it, straight away; the confirm is a
// dialog with a reason. A finished merge shows "Merged" with what moved, read from the confirm's own answer, and the
// preview is never run again on it; a preview whose duplicate is already merged shows that and offers nothing. A pair the
// list does not show (it pairs only shared aircraft and the account's own person) can still be merged by id, below the list.
// "Not a duplicate" hides a pair for good (the server stores and logs the answer); the hidden pairs are listed apart, each
// with Undo.
// Owner only: the server refuses everyone else; this page only hides itself.

const field = 'mt-1 block text-xs font-medium uppercase tracking-wide text-text-muted';
const ACTION_WORDS = {
  add_on_keep: 'added to the kept account', already_on_keep: 'already on the kept account', primary_on_keep: 'becomes the kept account\'s primary contact',
  contact_on_keep: 'added as a contact (the kept account has its own primary)', end_only_retired: 'ended (the aircraft is retired)',
};
const LATER_BY = { term_end: 'by term end', term_start: 'same term end, later start', only_one_has_a_term: 'the other has no term on file' };
const roleWord = (r) => String(r || '').replace(/_/g, ' ');
const errText = (e) => e?.body?.message || e?.message || 'failed';

function termText(p) {
  const s = formatHumanDate(p.effective_date); const e = formatHumanDate(p.expiration_date);
  if (s && e) return `${s} to ${e}`;
  if (e) return `ends ${e}`;
  if (s) return `starts ${s}`;
  return 'no term on file';
}

// TRACON's copy of Zoho's Created_Time (or TRACON's own date for an account with no Zoho id); null shows no Created line at all.
function createdText(c) {
  if (!c || !c.at) return null;
  return `${formatHumanDate(c.at)}${c.source === 'tracon' ? ' (in TRACON; not in Zoho)' : ''}`;
}

function AccountSide({ id, acct, later, onKeep, busy }) {
  const a = acct || {};
  return (
    <div data-pair-account={id} className="flex flex-col gap-2 rounded-card border border-border p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{a.name || id}</span>
        {later && <Badge variant="info">Later term</Badge>}
      </div>
      {createdText(a.created) && <div className="text-xs text-text-muted">Created {createdText(a.created)}</div>}
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Policies ({(a.policies || []).length})</div>
        <ul className="mt-1 list-disc pl-5">{(a.policies || []).map((p) => <li key={p.policy_id}>{p.name || p.policy_id}{p.stage ? `, ${p.stage}` : ''}, {termText(p)}</li>)}</ul>
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-text-muted">People ({(a.people || []).length})</div>
        <ul className="mt-1 list-disc pl-5">{(a.people || []).map((p) => <li key={p.person_id + p.role}>{p.name || p.person_id}, {p.role_word || roleWord(p.role)}</li>)}</ul>
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-text-muted">Aircraft ({(a.aircraft || []).length})</div>
        <div className="mt-1">{(a.aircraft || []).join(', ') || 'none'}</div>
      </div>
      <div className="mt-auto">
        <Button size="sm" variant="outline" disabled={busy} onClick={onKeep}>Merge into this one</Button>
      </div>
    </div>
  );
}

function PlanView({ plan }) {
  const m = plan.moves;
  return (
    <div data-merge-plan={plan.plan_hash} className="mt-3 flex flex-col gap-3 rounded-card border border-border bg-surface p-3 text-sm">
      <div><span className="text-text-muted">Merge</span> <span className="font-medium">{plan.duplicate.name}</span> <span className="text-text-dim">into</span> <span className="font-medium">{plan.keep.name}</span></div>
      {plan.refusals.length > 0 && (
        <div data-merge-refusals className="rounded-card border border-accent p-2">
          <div className="font-medium text-accent">This merge cannot run yet</div>
          <ul className="mt-1 list-disc pl-5">{plan.refusals.map((r) => <li key={r.code + r.message}>{r.message}</li>)}</ul>
        </div>
      )}
      <div>
        <div className="font-medium">Policies that move ({m.policies.length})</div>
        <ul className="mt-1 list-disc pl-5">{m.policies.map((p) => <li key={p.crm_deal_id || p.policy_id}>{p.name || p.crm_deal_id}{p.stage ? `, ${p.stage}` : ''}{p.from === 'zoho_only' ? ' (in Zoho only)' : ''}</li>)}</ul>
        <p className="mt-1 text-xs text-text-muted">{plan.travels.documents} filed document(s) travel with these policies.</p>
      </div>
      <div>
        <div className="font-medium">People ({m.people.length})</div>
        <ul className="mt-1 list-disc pl-5">{m.people.map((x) => (
          <li key={x.person_id}>{x.name || x.person_id}: {x.links.map((l) => `${roleWord(l.role)} ${ACTION_WORDS[l.action] || l.action}`).join('; ')}{x.main_moves ? '; main account moves to the kept one' : ''}{x.blocked ? ' (not moved)' : ''}</li>
        ))}</ul>
      </div>
      <div>
        <div className="font-medium">Aircraft ({m.aircraft.length})</div>
        <ul className="mt-1 list-disc pl-5">{m.aircraft.map((a) => <li key={a.link_id}>{a.registration || a.aircraft_id}: {ACTION_WORDS[a.action] || a.action}</li>)}</ul>
      </div>
      <div data-merge-account-documents>
        <div className="font-medium">The duplicate's own documents ({(m.account_documents || []).length})</div>
        <ul className="mt-1 list-disc pl-5">{(m.account_documents || []).map((d) => <li key={d.document_id}>{d.name}{d.original_filename && d.original_filename !== d.name ? ` (filed from ${d.original_filename})` : ''}: {d.action === 'move' ? 'moves to the kept account, after everything else has moved' : 'cannot move (see above)'}</li>)}</ul>
      </div>
      {plan.stays.length > 0 && (
        <div>
          <div className="font-medium">What stays</div>
          <ul className="mt-1 list-disc pl-5 text-text-muted">{plan.stays.map((s, i) => <li key={s.code + i}>{s.message}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** What the confirm's answer says moved, grouped (the answer is the record of the act; nothing is re-read or re-planned). */
export function movedGroups(result) {
  const moved = (result && result.moved) || [];
  const uniq = (xs) => [...new Set(xs.filter(Boolean))];
  return {
    policies: uniq(moved.filter((x) => x.what === 'policy').map((x) => x.name || x.id)),
    people: uniq(moved.filter((x) => x.what === 'person_link' || x.what === 'person_link_end').map((x) => `${x.name || x.person_id}, ${roleWord(x.role)}`)),
    mains: uniq(moved.filter((x) => x.what === 'main_account').map((x) => x.name || x.id)),
    aircraft: uniq(moved.filter((x) => x.what === 'aircraft' || x.what === 'aircraft_end').map((x) => x.registration || x.id)),
    documents: uniq(moved.filter((x) => x.what === 'account_document').map((x) => x.name || x.id)),
  };
}

function MovedList({ title, items }) {
  if (!items.length) return null;
  return <div><div className="font-medium">{title} ({items.length})</div><ul className="mt-1 list-disc pl-5">{items.map((t) => <li key={t}>{t}</li>)}</ul></div>;
}

function ResultView({ result }) {
  const g = movedGroups(result);
  return (
    <div data-merge-result={result.retired ? 'merged' : 'not_retired'} className="mt-3 flex flex-col gap-2 rounded-card border border-border bg-surface p-3 text-sm">
      <div className="font-semibold">{result.retired ? 'Merged' : 'Not merged: the duplicate was not retired'}</div>
      <div><span className="font-medium">{result.duplicate.name}</span> <span className="text-text-dim">into</span> <span className="font-medium">{result.keep.name}</span></div>
      <MovedList title="Policies moved" items={g.policies} />
      <MovedList title="People moved" items={g.people} />
      <MovedList title="Main account moved to the kept one" items={g.mains} />
      <MovedList title="Aircraft moved" items={g.aircraft} />
      <MovedList title="Documents moved" items={g.documents} />
      {!result.retired && <p className="text-text-muted">Still on the duplicate: {result.remaining.policies} policy(ies), {result.remaining.person_links} person link(s), {result.remaining.aircraft} aircraft link(s), {result.remaining.account_documents || 0} document(s).</p>}
      {result.not_moved.length > 0 && <ul className="list-disc pl-5">{result.not_moved.map((n, i) => <li key={n.id + i}>Not moved: {n.what.replace(/_/g, ' ')} {n.name || n.id}: {n.message}</li>)}</ul>}
    </div>
  );
}

function AlreadyMerged({ plan }) {
  return (
    <div data-merge-already className="mt-3 rounded-card border border-border bg-surface p-3 text-sm">
      <div className="font-semibold">Merged</div>
      <p className="mt-1 text-text-muted">{plan.duplicate.name} is already merged{plan.duplicate.merged_into_id === plan.keep.id ? ` into ${plan.keep.name}` : ' into another account'}. There is nothing left to preview.</p>
    </div>
  );
}

/**
 * One merge in flight: the preview of keeping `keepId` and merging `dupId` into it, the confirm dialog, then the answer.
 * A finished merge (retired) shows the answer and stops; the preview is not run again.
 */
function useMergeFlow(onMerged) {
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const preview = useCallback(async (dupId, keepId) => {
    setBusy(true); setResult(null); setPlan(null);
    try { setPlan((await getMergePreview(String(dupId).trim(), String(keepId).trim())).plan); } catch (e) { toast.error(`No preview: ${errText(e)}`); } finally { setBusy(false); }
  }, []);

  async function confirm() {
    setBusy(true);
    try {
      const r = await confirmMerge({ duplicateId: plan.duplicate.id, keepId: plan.keep.id, reason: reason.trim(), planHash: plan.plan_hash });
      for (const w of r.warnings || []) toast.info(w);
      setOpen(false); setReason(''); setPlan(null); setResult(r);
      if (r.retired) { toast.success('Merged. The duplicate is retired.'); if (onMerged) onMerged(r); } else toast.info('Not everything moved; the duplicate was not retired.');
    } catch (e) {
      if (e?.body?.plan) { setPlan(e.body.plan); setOpen(false); }
      toast.error(`Not merged: ${errText(e)}`);
    } finally { setBusy(false); }
  }

  const merged = !!(plan && (plan.already_merged || (plan.duplicate && plan.duplicate.merged_into_id)));
  const view = (
    <>
      {result && <ResultView result={result} />}
      {!result && plan && merged && <AlreadyMerged plan={plan} />}
      {!result && plan && !merged && (
        <>
          <PlanView plan={plan} />
          <div className="mt-2 flex gap-2">
            <Button size="sm" disabled={busy || plan.refusals.length > 0} onClick={() => setOpen(true)}>Merge...</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setPlan(null)}>Close the preview</Button>
          </div>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge {plan ? plan.duplicate.name : ''} into {plan ? plan.keep.name : ''}?</DialogTitle>
            <DialogDescription>
              {plan ? `${plan.counts.policies} policy(ies), ${plan.counts.person_links} person link(s), ${plan.counts.aircraft} aircraft link(s) and ${plan.counts.account_documents || 0} of the duplicate's own document(s) move. The duplicate is retired only if everything moves.` : ''}
            </DialogDescription>
          </DialogHeader>
          <label className={field}>Reason (kept on the activity line)
            <Input aria-label="Merge reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
          </label>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={busy || !reason.trim()} onClick={confirm}>{busy ? 'Merging...' : 'Merge'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
  return { preview, busy, view, done: !!(result && result.retired) };
}

function PairCard({ pair, accounts, onMerged, onAnswered }) {
  const flow = useMergeFlow(onMerged);
  const [answering, setAnswering] = useState(false);
  async function notDuplicate() {
    setAnswering(true);
    try { const r = await markNotDuplicate(pair.account_ids); if (r.hidden) { toast.success('Hidden: not a duplicate. Undo it from the hidden pairs below.'); onAnswered(); } else toast.info('The answer could not be read back; reload to check.'); }
    catch (e) { toast.error(`Not hidden: ${errText(e)}`); } finally { setAnswering(false); }
  }
  const [a, b] = pair.account_ids;
  const later = pair.later_term && pair.later_term.account_id;
  const laterName = later ? ((accounts[later] && accounts[later].name) || later) : null;
  return (
    <div data-review-pair={pair.account_ids.join('|')} className="rounded-card border border-border bg-surface p-3 text-sm">
      <div className="text-xs text-text-muted">
        {pair.reasons.map((r) => `${r.label} (${r.count})`).join(', ')}{pair.related ? ', already related' : ''}
        {pair.later_term && (laterName ? `. ${laterName} has the later term (${LATER_BY[pair.later_term.by] || pair.later_term.by}).` : `. Neither account has the later term (${pair.later_term.by === 'same_term' ? 'the same term on both' : 'no term on either'}).`)}
      </div>
      {!flow.done && (
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <AccountSide id={a} acct={accounts[a]} later={later === a} busy={flow.busy} onKeep={() => flow.preview(b, a)} />
          <AccountSide id={b} acct={accounts[b]} later={later === b} busy={flow.busy} onKeep={() => flow.preview(a, b)} />
        </div>
      )}
      {!flow.done && <div className="mt-2"><Button size="sm" variant="outline" disabled={flow.busy || answering} onClick={notDuplicate}>{answering ? 'Saving...' : 'Not a duplicate'}</Button></div>}
      {flow.view}
    </div>
  );
}

function HiddenPairs({ hidden, accounts, onAnswered }) {
  const [busy, setBusy] = useState(null);
  if (!hidden || !hidden.length) return null;
  const name = (id) => (accounts[id] && accounts[id].name) || id;
  async function undo(p) {
    const key = p.account_ids.join('|');
    setBusy(key);
    try { const r = await undoNotDuplicate(p.account_ids); if (!r.hidden) { toast.success('Back on the list.'); onAnswered(); } else toast.info('The answer could not be read back; reload to check.'); }
    catch (e) { toast.error(`Not undone: ${errText(e)}`); } finally { setBusy(null); }
  }
  return (
    <details className="mt-6 rounded-card border border-border p-3 text-sm">
      <summary className="cursor-pointer font-medium">Hidden as not a duplicate ({hidden.length})</summary>
      <div className="mt-2 flex flex-col gap-2">
        {hidden.map((p) => (
          <div key={p.account_ids.join('|')} data-hidden-pair={p.account_ids.join('|')} className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-border p-2">
            <div>
              <div>{name(p.account_ids[0])} <span className="text-text-dim">and</span> {name(p.account_ids[1])}</div>
              <div className="text-xs text-text-muted">{p.reasons.map((r) => r.label).join(', ')}. Hidden {p.answer && p.answer.at ? formatHumanDate(p.answer.at) : ''}{p.answer && p.answer.actor ? ` by ${p.answer.actor}` : ''}{p.answer && p.answer.reason ? `: ${p.answer.reason}` : ''}</div>
            </div>
            <Button size="sm" variant="outline" disabled={busy === p.account_ids.join('|')} onClick={() => undo(p)}>{busy === p.account_ids.join('|') ? 'Saving...' : 'Undo'}</Button>
          </div>
        ))}
      </div>
    </details>
  );
}

function ByIdMerge({ onMerged }) {
  const flow = useMergeFlow(onMerged);
  const [dupId, setDupId] = useState('');
  const [keepId, setKeepId] = useState('');
  return (
    <details className="mt-6 rounded-card border border-border p-3 text-sm">
      <summary className="cursor-pointer font-medium">Merge a pair the list does not show</summary>
      <p className="mt-1 text-xs text-text-muted">The list pairs accounts sharing an aircraft or the account's own person. For any other pair, enter both ids (TRACON or Zoho account id).</p>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className={field}>Duplicate (merged away)
          <Input aria-label="Duplicate account id" value={dupId} onChange={(e) => setDupId(e.target.value)} className="mt-1" />
        </label>
        <label className={field}>Account to keep
          <Input aria-label="Account to keep id" value={keepId} onChange={(e) => setKeepId(e.target.value)} className="mt-1" />
        </label>
      </div>
      <div className="mt-2"><Button size="sm" variant="outline" disabled={flow.busy || !dupId.trim() || !keepId.trim()} onClick={() => flow.preview(dupId, keepId)}>{flow.busy ? 'Reading...' : 'Preview'}</Button></div>
      {flow.view}
    </details>
  );
}

export default function AccountMergeAdmin() {
  const me = useMe();
  const [review, setReview] = useState(null);
  const [merged, setMerged] = useState([]);

  const load = useCallback(() => getReviewList().then(setReview).catch((e) => toast.error(`The review list could not be read: ${errText(e)}`)), []);
  useEffect(() => { load(); }, [load]);
  // A finished merge stays on the page as "Merged" with what moved; its pair drops off the open list on the reload.
  const onMerged = useCallback((r) => { setMerged((m) => [r, ...m]); load(); }, [load]);

  if (me && !ownerPagesVisible(me)) return <div className="p-4 text-sm text-text-muted">Only the owner sees this page.</div>;
  const c = review && review.counts;
  return (
    <div className="max-w-5xl p-4">
      <h2 className="text-lg font-semibold text-text">Merge accounts</h2>
      <p className="mt-1 text-sm text-text-muted">The duplicate's policies, people, aircraft and its own documents move to the account you keep, through Zoho as well. The duplicate is then retired: hidden, not deleted, pointing at the kept account. Each merge shows its preview first; nothing moves until you confirm.</p>
      {merged.map((r) => <ResultView key={`${r.duplicate.id}>${r.keep.id}`} result={r} />)}
      {review && (
        <div className="mt-4">
          <h3 className="text-base font-semibold text-text">Accounts that look duplicated ({c.pairs})</h3>
          <p className="mt-1 text-xs text-text-muted">Same aircraft on two accounts (possible sale or duplicate): {c.same_aircraft}. Same primary contact or named insured on two accounts: {c.same_person}. Already related: {c.related}. Hidden as not a duplicate: {c.hidden || 0}. Nothing here is decided.</p>
          <div className="mt-2 flex flex-col gap-3">
            {review.pairs.map((p) => <PairCard key={p.account_ids.join('|')} pair={p} accounts={review.accounts || {}} onMerged={onMerged} onAnswered={load} />)}
          </div>
          <HiddenPairs hidden={review.hidden} accounts={review.accounts || {}} onAnswered={load} />
        </div>
      )}
      <ByIdMerge onMerged={onMerged} />
    </div>
  );
}
